import React from 'react';
import PropTypes from 'prop-types';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  container: {
    marginTop: 5,
    paddingHorizontal: 6,
  },
  block: {
    marginBottom: 4,
  },
  unstyled: {
    fontSize: 10,
    lineHeight: 1.4,
  },
  unorderedListItem: {
    fontSize: 10,
    lineHeight: 1.4,
    marginLeft: 10,
  },
  orderedListItem: {
    fontSize: 10,
    lineHeight: 1.4,
    marginLeft: 10,
  },
  headerOne: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    marginTop: 8,
    marginBottom: 4,
  },
  headerTwo: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    marginTop: 6,
    marginBottom: 3,
  },
  blockquote: {
    fontSize: 10,
    lineHeight: 1.4,
    fontStyle: 'italic',
    color: '#555',
    paddingLeft: 8,
    marginLeft: 4,
    borderLeftWidth: 2,
    borderLeftColor: '#ccc',
    marginTop: 4,
    marginBottom: 4,
  },
});

// Segment a block of text into distinct `<Text>` chunks based on Draft.js inline style overlaps
const renderInlineStyles = (text, inlineStyleRanges) => {
  if (!inlineStyleRanges || inlineStyleRanges.length === 0) return text;

  // Map each character to an array of applied styles
  const chars = text.split('').map(char => ({ char, styles: [] }));

  inlineStyleRanges.forEach(range => {
    for (let i = range.offset; i < range.offset + range.length; i++) {
      if (chars[i]) {
        chars[i].styles.push(range.style);
      }
    }
  });

  // Group contiguous characters sharing the exact same styles into text segments
  const segments = [];
  let currentSegment = { text: '', styles: [] };

  chars.forEach(c => {
    const isSameStyle =
      currentSegment.styles.length === c.styles.length &&
      currentSegment.styles.every(s => c.styles.includes(s));

    if (isSameStyle) {
      currentSegment.text += c.char;
    } else {
      if (currentSegment.text) segments.push(currentSegment);
      currentSegment = { text: c.char, styles: c.styles };
    }
  });
  if (currentSegment.text) segments.push(currentSegment);

  // Return an array of React PDF <Text> components with their exact styles applied
  return segments.map((seg, idx) => {
    const isBold = seg.styles.includes('BOLD');
    const isItalic = seg.styles.includes('ITALIC');
    
    const extraStyle = {};
    if (seg.styles.includes('UNDERLINE')) extraStyle.textDecoration = 'underline';

    // @react-pdf/renderer aggressively evaluates strict font bounds! It often rejects `fontWeight: bold` on nested <Text> sub-nodes.
    // Instead of hoping it inherits properly, we explicitly assign the literal PostScript name for Helvetica!
    if (isBold && isItalic) {
      extraStyle.fontFamily = 'Helvetica-BoldOblique';
    } else if (isBold) {
      extraStyle.fontFamily = 'Helvetica-Bold';
    } else if (isItalic) {
      extraStyle.fontFamily = 'Helvetica-Oblique';
    }

    // If no styles are active, just yield the string
    if (Object.keys(extraStyle).length === 0) return seg.text;

    return (
      <Text key={idx} style={extraStyle}>
        {seg.text}
      </Text>
    );
  });
};

const DraftTextRenderer = ({ rawContent }) => {
  if (!rawContent || !rawContent.blocks || rawContent.blocks.length === 0) {
    return null;
  }

  let orderedListIndex = 1;

  return (
    <View style={styles.container}>
      {rawContent.blocks.map((block, i) => {
        const { text, type, inlineStyleRanges } = block;

        // Reset ordered list index if sequence breaks
        if (type !== 'ordered-list-item') {
          orderedListIndex = 1;
        }

        // Skip completely empty unstyled layout blocks acting as trailing spacers
        if (!text && type === 'unstyled') {
          return <View key={i} style={{ height: 6 }} />;
        }

        let blockStyle = styles.unstyled;
        let prefix = '';

        if (type === 'unordered-list-item') {
          blockStyle = styles.unorderedListItem;
          prefix = '•  ';
        } else if (type === 'ordered-list-item') {
          blockStyle = styles.orderedListItem;
          prefix = `${orderedListIndex}.  `;
          orderedListIndex++;
        } else if (type === 'header-one') {
          blockStyle = styles.headerOne;
        } else if (type === 'header-two' || type === 'header-three') {
          blockStyle = styles.headerTwo;
        } else if (type === 'blockquote') {
          blockStyle = styles.blockquote;
        }

        // Render prefix and exactly-segmented text strings!
        return (
          <View key={i} style={[styles.block, blockStyle]}>
            <Text>
              {prefix}
              {renderInlineStyles(text, inlineStyleRanges)}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

DraftTextRenderer.propTypes = {
  rawContent: PropTypes.object,
};

export default DraftTextRenderer;
